import { Lease, calculateYearlyIncome } from '../../types/leases';
import { LeaseObjectType } from '../../types/Enums';
import { naturalCompare } from '../../Helpers/naturalSort';
import { LeaseStatus, coTenantEntries, leaseObjectLabel, leaseStatus, noticeDeadline, todayStockholm } from '../../Helpers/leaseUtils';

/**
 * Pure view logic of Hyresavtal (/leases): filters, sorting and the summary
 * tiles. The page and its hook only wire these to state and the URL.
 */

export interface LeaseFilters {
    /** Empty = every building. */
    buildingKeys: string[];
    search: string;
    /** Empty = every type. */
    objectTypes: LeaseObjectType[];
    showEnded: boolean;
}

export interface LeaseNames {
    buildingName: (buildingKey: string) => string;
    tenantName: (tenantKey: string) => string;
    /** The object as shown (with the unit's stairwell); defaults to leaseObjectLabel. */
    objectLabel?: (lease: Lease) => string;
}

/** The object type filter's choices, in the order shown. */
export const OBJECT_TYPE_FILTERS: LeaseObjectType[] = [
    LeaseObjectType.Apartment,
    LeaseObjectType.CommercialUnit,
    LeaseObjectType.ParkingSpot,
    LeaseObjectType.StorageUnit,
    LeaseObjectType.Other,
];

/** Leases in the chosen buildings and object types matching the search; ended ones only with `showEnded`. */
export const filterLeases = (leases: Lease[], filters: LeaseFilters, names: LeaseNames): Lease[] => {
    const buildings = new Set(filters.buildingKeys);
    const types = new Set(filters.objectTypes);
    const needle = filters.search.trim().toLowerCase();
    return leases.filter(lease => {
        if (buildings.size > 0 && !buildings.has(lease.buildingKey)) return false;
        if (types.size > 0 && !types.has(lease.object.type)) return false;
        if (!filters.showEnded && leaseStatus(lease) === 'ended') return false;
        if (!needle) return true;
        const haystack = [
            (names.objectLabel ?? leaseObjectLabel)(lease),
            names.tenantName(lease.tenantKey),
            ...coTenantEntries(lease).map(entry => names.tenantName(entry.tenantKey)),
        ];
        return haystack.some(text => text.toLowerCase().includes(needle));
    });
};

export type LeaseSortKey = 'building' | 'object' | 'tenant' | 'period' | 'amount' | 'status';

export interface LeaseSort {
    key: LeaseSortKey;
    direction: 'asc' | 'desc';
}

export const DEFAULT_LEASE_SORT: LeaseSort = { key: 'building', direction: 'asc' };

const STATUS_ORDER: Record<LeaseStatus, number> = { active: 0, upcoming: 1, ended: 2 };

const compareBy = (key: LeaseSortKey, names: LeaseNames) => (a: Lease, b: Lease): number => {
    switch (key) {
        case 'building': return naturalCompare(names.buildingName(a.buildingKey), names.buildingName(b.buildingKey));
        case 'object': {
            const label = names.objectLabel ?? leaseObjectLabel;
            return naturalCompare(label(a), label(b));
        }
        case 'tenant': return naturalCompare(names.tenantName(a.tenantKey), names.tenantName(b.tenantKey));
        case 'period': return a.startDate.localeCompare(b.startDate);
        case 'amount': return calculateYearlyIncome(a.amount, a.interval) - calculateYearlyIncome(b.amount, b.interval);
        case 'status': return STATUS_ORDER[leaseStatus(a)] - STATUS_ORDER[leaseStatus(b)];
    }
};

/**
 * Sorted copy: the chosen column first, then building and object so equal
 * rows keep a stable, readable order.
 */
export const sortLeases = (leases: Lease[], sort: LeaseSort, names: LeaseNames): Lease[] => {
    const primary = compareBy(sort.key, names);
    const sign = sort.direction === 'asc' ? 1 : -1;
    const tieBreak = [compareBy('building', names), compareBy('object', names)];
    return [...leases].sort((a, b) => {
        const result = primary(a, b) * sign;
        if (result !== 0) return result;
        for (const compare of tieBreak) {
            const tie = compare(a, b);
            if (tie !== 0) return tie;
        }
        return 0;
    });
};

/** Clicking a header: the same column flips direction, a new one starts ascending. */
export const nextSort = (current: LeaseSort, key: LeaseSortKey): LeaseSort =>
    current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' };

export interface LeaseSummary {
    /** Upcoming notice deadlines, soonest first, at most five. */
    upcomingDeadlines: { lease: Lease; deadline: string }[];
}

/** The deadline tile, over the leases in the chosen buildings and types. */
export const leaseSummary = (leases: Lease[], today: string = todayStockholm()): LeaseSummary => ({
    upcomingDeadlines: leases
        .filter(lease => leaseStatus(lease, today) !== 'ended')
        .map(lease => ({ lease, deadline: noticeDeadline(lease) }))
        .filter((entry): entry is { lease: Lease; deadline: string } => !!entry.deadline && entry.deadline >= today)
        .sort((a, b) => a.deadline.localeCompare(b.deadline))
        .slice(0, 5),
});

/** `/leases?building=a&building=b`: the building filter, deduplicated, empty = all. */
export const buildingKeysFromParams = (params: URLSearchParams): string[] =>
    Array.from(new Set(params.getAll('building').filter(Boolean)));

/** The period the Hyra column shows every lease's rent per, whatever its own interval. */
export type RentPeriod = 'month' | 'quarter' | 'halfYear' | 'year';

export const RENT_PERIODS: { key: RentPeriod; name: string; months: number }[] = [
    { key: 'month', name: 'Per månad', months: 1 },
    { key: 'quarter', name: 'Per kvartal', months: 3 },
    { key: 'halfYear', name: 'Per halvår', months: 6 },
    { key: 'year', name: 'Per år', months: 12 },
];

export const DEFAULT_RENT_PERIOD: RentPeriod = 'month';

/** "Hyra per månad": the Hyra column's header for the chosen period. */
export const rentHeader = (period: RentPeriod): string =>
    `Hyra ${(RENT_PERIODS.find(p => p.key === period) ?? RENT_PERIODS[0]).name.toLowerCase()}`;

/** The lease's rent converted to the period: 1 000 kr per month is 12 000 kr per year, 3 000 kr per quarter. */
export const rentPer = (lease: Pick<Lease, 'amount' | 'interval'>, period: RentPeriod): number => {
    const months = (RENT_PERIODS.find(p => p.key === period) ?? RENT_PERIODS[0]).months;
    return (calculateYearlyIncome(lease.amount, lease.interval) * months) / 12;
};

/**
 * The table's Summa row: the rent of the active leases among the rows shown,
 * per the chosen period. Upcoming and ended rows are listed but not summed.
 */
export const activeRentTotal = (
    leases: Lease[],
    period: RentPeriod,
    today: string = todayStockholm()
): { activeCount: number; total: number } => {
    const active = leases.filter(lease => leaseStatus(lease, today) === 'active');
    return { activeCount: active.length, total: active.reduce((sum, lease) => sum + rentPer(lease, period), 0) };
};
